import * as React from "react";
import { EditableTable } from "../../../../../../components/Tables/NewEditableTable";
import { Box, Typography } from "@mui/material";
import { useMIS } from "../MISContext";

const fontSize = "14px";
const cellPadding = "12px";

const monthKeys = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

const parseValidDate = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toMonthStart = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

/** Build month columns oldest → newest from earliest through latest (max 12 months). */
const buildOrderedMonths = (earliestDate, latestDate) => {
  const currentYear = new Date().getFullYear();
  const makeEntry = (key, year) => ({ key, year, id: `${year}-${key}` });

  if (!earliestDate) {
    return monthKeys.map((key) => makeEntry(key, currentYear));
  }

  const start = toMonthStart(earliestDate);
  let end = latestDate ? toMonthStart(latestDate) : start;
  if (end < start) end = start;

  const fullRange = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    fullRange.push(makeEntry(monthKeys[cursor.getMonth()], cursor.getFullYear()));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  if (fullRange.length === 0) {
    return monthKeys.map((key) => makeEntry(key, currentYear));
  }

  let result = fullRange;
  if (result.length > 12) {
    result = result.slice(-12);
  } else if (result.length < 12) {
    const padded = [...result];
    while (padded.length < 12) {
      const last = padded[padded.length - 1];
      let monthIndex = monthKeys.indexOf(last.key) + 1;
      let year = last.year;
      if (monthIndex >= 12) {
        monthIndex = 0;
        year += 1;
      }
      padded.push(makeEntry(monthKeys[monthIndex], year));
    }
    result = padded;
  }

  return result;
};

export default function ReceivablesTab() {
  const {
    receivablesRows,
    setReceivablesRows,
    hoursRows: contextHoursRows,
    poPayablesData,
    procurementCalculatedProgress,
    totalAvgProgress,
    loading,
    poPayablesLoading,
  } = useMIS();

  // Month columns: chronological from earliest to latest date across tasks and payments
  const orderedMonths = React.useMemo(() => {
    let earliestDate = null;
    let latestDate = null;

    const considerDate = (dateValue) => {
      const d = parseValidDate(dateValue);
      if (!d) return;
      if (!earliestDate || d < earliestDate) earliestDate = d;
      if (!latestDate || d > latestDate) latestDate = d;
    };

    (Array.isArray(receivablesRows) ? receivablesRows : []).forEach((row) => {
      considerDate(row?.firstDate);
    });

    (Array.isArray(poPayablesData) ? poPayablesData : []).forEach((payment) => {
      considerDate(payment?.paymentDate ?? payment?.payment_date);
    });

    return buildOrderedMonths(earliestDate, latestDate);
  }, [receivablesRows, poPayablesData]);

  // Helper to get month key from date
  const getMonthKey = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    return monthKeys[date.getMonth()] || null;
  };

  // Combined Hours table: Engineering + Fabrication from context hoursRows (actualHours)
  const hoursRows = React.useMemo(() => {
    const engineeringRow = (contextHoursRows || []).find(
      (row) => row.task?.toLowerCase() === "engineering",
    );
    const fabricationRow = (contextHoursRows || []).find(
      (row) => row.task?.toLowerCase() === "fabrication",
    );

    // Build Engineering row with all months
    const engData = { id: 1, discipline: "Engineering" };
    monthKeys.forEach((key) => {
      engData[key] = engineeringRow?.[key] || "";
    });
    const engTotal = monthKeys.reduce(
      (sum, key) => sum + (Number(engineeringRow?.[key]) || 0),
      0,
    );

    // Build Fabrication row with all months
    const fabData = { id: 2, discipline: "Fabrication" };
    monthKeys.forEach((key) => {
      fabData[key] = fabricationRow?.[key] || "";
    });
    const fabTotal = monthKeys.reduce(
      (sum, key) => sum + (Number(fabricationRow?.[key]) || 0),
      0,
    );

    // Build Total row - sum of Engineering and Fabrication for each month
    const totalData = { id: 3, discipline: "Total" };
    monthKeys.forEach((key) => {
      const engVal = Number(engineeringRow?.[key]) || 0;
      const fabVal = Number(fabricationRow?.[key]) || 0;
      totalData[key] = engVal + fabVal || "";
    });
    totalData.total = engTotal + fabTotal;

    return [engData, fabData, totalData];
  }, [contextHoursRows]);

  // Extract Material & Subcontractor cost from poPayablesData (payables)
  const costRows = React.useMemo(() => {
    // Group payables by month
    const monthlyTotals = {};
    monthKeys.forEach((key) => {
      monthlyTotals[key] = 0;
    });

    (poPayablesData || []).forEach((payment) => {
      const date = payment.paymentDate ?? payment.payment_date;
      const monthKey = getMonthKey(date);
      if (monthKey && monthKeys.includes(monthKey)) {
        monthlyTotals[monthKey] += Number(payment.amount ?? payment.paymentAmount) || 0;
      }
    });

    const dataRow = { id: 1, discipline: "Procurement" };
    monthKeys.forEach((key) => {
      dataRow[key] = monthlyTotals[key];
    });

    const total = monthKeys.reduce(
      (sum, key) => sum + (monthlyTotals[key] || 0),
      0,
    );

    const totalRow = { id: 2, discipline: "Total" };
    monthKeys.forEach((key) => {
      totalRow[key] = monthlyTotals[key];
    });
    totalRow.total = total;

    return [dataRow, totalRow];
  }, [poPayablesData]);

  const progressColumnOrder = React.useMemo(
    () => [
      "task",
      ...orderedMonths.map(({ id }) => id),
      "cumulative-progress",
      "mrt-row-actions",
    ],
    [orderedMonths],
  );

  const columns = React.useMemo(() => {
    const monthLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

    const cumulativeFooters = {};
    monthKeys.forEach((key) => {
      const values = receivablesRows.map((r) => Number(r[key]) || 0);
      const avg = values.length
        ? values.reduce((a, b) => a + b, 0) / values.length
        : 0;
      cumulativeFooters[key] = avg;
    });

    const monthColumns = orderedMonths.map(({ key, year, id }) => ({
      id,
      accessorFn: (row) => row[key],
      accessorKey: key,
      header: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            lineHeight: 1.1,
          }}
        >
          <span>{monthLabel(key)}</span>
          <Typography variant="caption" component="span">
            {year}
          </Typography>
        </Box>
      ),
      size: 50,
      // Average progress for tasks dated in this month (same task.progress source as TaskList)
      Cell: ({ row }) => {
        const v = Number(row.original[key]) || 0;
        return v > 0 ? `${v.toFixed(2)}%` : "";
      },
      footer: cumulativeFooters[key]
        ? `${cumulativeFooters[key].toFixed(2)}%`
        : "0.00%",
      muiTableHeadCellProps: {
        sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
      },
      muiTableBodyCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
      muiTableFooterCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
    }));

    return [
      {
        id: "task",
        accessorKey: "task",
        header: "Tasks",
        size: 180,
        footer: `Avg Progress`,
        muiTableHeadCellProps: {
          sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableFooterCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
      ...monthColumns,
      {
        id: "cumulative-progress",
        accessorKey: "averageProgress",
        header: "Cumulative Progress",
        size: 60,
        enableEditing: false,
        Cell: ({ row }) => {
          const taskCount = row.original.taskCount ?? 0;
          if (!taskCount) return "";
          const avgProgress = Number(row.original.averageProgress) || 0;
          return `${avgProgress.toFixed(2)}%`;
        },
        footer: totalAvgProgress ? `${totalAvgProgress}%` : "0.00%",
        muiTableHeadCellProps: {
          sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableFooterCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
    ];
  }, [receivablesRows, orderedMonths, totalAvgProgress]);

  // Columns for Hours table (Engineering + Fabrication) - all 12 months
  const hoursColumns = React.useMemo(() => {
    const monthLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

    const monthCols = orderedMonths.map(({ key, year, id }) => ({
      id,
      accessorKey: key,
      header: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            lineHeight: 1.1,
          }}
        >
          <span>{monthLabel(key)}</span>
          <Typography variant="caption" component="span">
            {year}
          </Typography>
        </Box>
      ),
      size: 50,
      muiTableHeadCellProps: {
        sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
      },
      muiTableBodyCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
    }));

    return [
      {
        accessorKey: "discipline",
        header: "Discipline",
        size: 120,
        muiTableHeadCellProps: {
          sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
      ...monthCols,
      {
        id: "total",
        header: "Total",
        size: 70,
        accessorFn: (row) =>
          monthKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0),
        muiTableHeadCellProps: {
          sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
    ];
  }, [orderedMonths]);

  // Columns for Cost table (Material & Subcontractor) - all 12 months
  const costColumns = React.useMemo(() => {
    const monthLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

    const monthCols = orderedMonths.map(({ key, year, id }) => ({
      id,
      accessorKey: key,
      header: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            lineHeight: 1.1,
          }}
        >
          <span>{monthLabel(key)}</span>
          <Typography variant="caption" component="span">
            {year}
          </Typography>
        </Box>
      ),
      size: 50,
      Cell: ({ row }) => {
        const v = Number(row.original[key]) || 0;
        return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
      },
      muiTableHeadCellProps: {
        sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
      },
      muiTableBodyCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
    }));

    return [
      {
        accessorKey: "discipline",
        header: "Category",
        size: 120,
        muiTableHeadCellProps: {
          sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
      ...monthCols,
      {
        id: "total",
        header: "Total",
        size: 70,
        accessorFn: (row) =>
          monthKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0),
        Cell: ({ row }) => {
          const total = monthKeys.reduce(
            (sum, key) => sum + (Number(row.original[key]) || 0),
            0,
          );
          return `$${total.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
        },
        muiTableHeadCellProps: {
          sx: { fontSize: "0.95rem", fontWeight: "bold", padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
    ];
  }, [orderedMonths]);

  const hooks = React.useMemo(
    () => ({
      useCreateItem: () => ({
        mutateAsync: async ({ values }) => {
          setReceivablesRows((prev) => [
            ...prev,
            { ...values, id: Date.now() },
          ]);
        },
        isPending: false,
      }),
      useGetItems: () => ({
        data: receivablesRows,
        isError: false,
        isFetching: false,
        isLoading: loading,
      }),
      useUpdateItem: () => ({
        mutateAsync: async ({ values }) => {
          setReceivablesRows((prev) =>
            prev.map((row) => (row.id === values.id ? values : row)),
          );
        },
        isPending: false,
      }),
      useDeleteItem: () => ({
        mutateAsync: async ({ id }) => {
          setReceivablesRows((prev) => prev.filter((row) => row.id !== id));
        },
        isPending: false,
      }),
    }),
    [receivablesRows, setReceivablesRows],
  );

  // Read-only hooks for Hours table (Engineering + Fabrication)
  const hoursHooks = React.useMemo(
    () => ({
      useCreateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useGetItems: () => ({
        data: hoursRows,
        isError: false,
        isFetching: false,
        isLoading: loading,
      }),
      useUpdateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useDeleteItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
    }),
    [hoursRows],
  );

  // Read-only hooks for Cost table (Material & Subcontractor)
  const costHooks = React.useMemo(
    () => ({
      useCreateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useGetItems: () => ({
        data: costRows,
        isError: false,
        isFetching: false,
        isLoading: poPayablesLoading,
      }),
      useUpdateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useDeleteItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
    }),
    [costRows],
  );

  return (
    <Box
      width="100%"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography variant="h6" align="center" sx={{ fontWeight: 600, mb: 1 }}>
        Account receivables
      </Typography>

      {/* Top left large table: % Progress complete */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: { xs: "100%", md: "70%" } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            % Progress complete
          </Typography>
          <EditableTable
            name="Receivables"
            columns={columns}
            hooks={hooks}
            data={receivablesRows}
            editMode="cell"
            enableEditing={true}
            enableDelete={true}
            additionalProps={{
              enableColumnOrdering: false,
              enableColumnDragging: false,
              enableSorting: false,
              state: {
                columnOrder: progressColumnOrder,
              },
              initialState: {
                columnOrder: progressColumnOrder,
              },
            }}
            initialData={{
              task: "",
              jan: "",
              feb: "",
              mar: "",
              apr: "",
              may: "",
              jun: "",
              jul: "",
              aug: "",
              sep: "",
              oct: "",
              nov: "",
              dec: "",
            }}
          />
        </Box>
      </Box>

      {/* Bottom two tables: Hours (Engineering + Fabrication) and Cost */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Hours table - Engineering + Fabrication */}
        <Box sx={{ width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Hours (Engineering &amp; Fabrication)
          </Typography>
          <EditableTable
            name="Hours"
            columns={hoursColumns}
            hooks={hoursHooks}
            editMode="cell"
            enableEditing={false}
            enableDelete={false}
            initialData={{
              discipline: "",
              ...monthKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
            }}
            data={hoursRows}
          />
        </Box>

        {/* Cost table - Material & Subcontractor */}
        <Box sx={{ width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Material &amp; Subcontractor Cost
          </Typography>
          {procurementCalculatedProgress != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Procurement Progress (from MRQ/PO/MRV):{" "}
              {Number(procurementCalculatedProgress).toFixed(1)}%
            </Typography>
          )}
          <EditableTable
            name="Cost"
            columns={costColumns}
            hooks={costHooks}
            editMode="cell"
            enableEditing={false}
            enableDelete={false}
            initialData={{
              discipline: "",
              ...monthKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
            }}
            data={costRows}
          />
        </Box>
      </Box>
    </Box>
  );
}
